import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    ReactGrid,
    type CellChange as ReactGridCellChange,
    type Id,
} from "@silevis/reactgrid";
import { selectActiveSheet, updateCells } from "../engine/gridSlice";
import { getVisibleRows } from "../engine/selectors";
import { translateRows, translateColumns } from "./mapper";
import { FormulaCellTemplate } from "./cellTemplates/formulaTemplate";
import {
    ChevronCellTemplate,
    type ChevronRowAction,
} from "./cellTemplates/chevronTemplate";
import { DropdownCellTemplate } from "./cellTemplates/dropdownTemplate";
import { AddButtonCellTemplate } from "./cellTemplates/addButtonTemplate";
import { DynamicRateCellTemplate } from "./cellTemplates/dynamicRateTemplate";
import { IconHeaderCellTemplate } from "./cellTemplates/iconHeaderTemplate";
import { PercentageCellTemplate } from "./cellTemplates/percentageCellTemplate";
import { StatusDotCellTemplate } from "./cellTemplates/statusDotTemplate";
import type { ServiceItem } from "./cellTemplates/addButtonTemplate";
import type { CellChange, SheetCell } from "../engine/cells";

import "@silevis/reactgrid/styles.css";

/**
 * Generic View Component parsing Headless Spreadsheet Engine Data
 * and transforming it into ReactGrid DOM.
 */
export const SpreadsheetView: React.FC<{
    onRowAction?: (action: string) => void;
    onCellsChanged?: (changes: CellChange[]) => void;
    onRowsReordered?: (
        targetRowId: string,
        rowIds: string[],
        dropPosition: any,
    ) => void;
    onCellContextMenu?: (
        e: React.MouseEvent,
        location: { rowId: string; columnId: string } | null,
    ) => void;
    focusLocation?: { rowId: string; columnId: string } | null;
    serviceItems?: ServiceItem[];
    internalServiceItems?: ServiceItem[];
    onChevronAction?: (action: ChevronRowAction) => void;
}> = ({
    onRowAction,
    onCellsChanged,
    onRowsReordered,
    onCellContextMenu,
    focusLocation,
    serviceItems = [],
    internalServiceItems = [],
    onChevronAction,
}) => {
    const dispatch = useDispatch();
    const currentSheet = useSelector(selectActiveSheet);

    const visibleRows = useMemo(
        () => getVisibleRows(currentSheet),
        [currentSheet],
    );

    // Track the current focused cell for context menus
    const focusedLocationRef = React.useRef<{
        rowId: string;
        columnId: string;
    } | null>(null);

    // DOM Generation
    const translatedColumns = useMemo(
        () => translateColumns(currentSheet.columns),
        [currentSheet.columns],
    );
    const translatedRows = useMemo(
        () => translateRows(visibleRows),
        [visibleRows],
    );

    const handleButtonAction = React.useCallback(
        (action: string) => {
            if (onRowAction) onRowAction(action);
        },
        [onRowAction],
    );

    const handleCellsChanged = (changes: ReactGridCellChange[]) => {
        const mappedChanges: any[] = [];
        changes.forEach((c) => {
            const newCell = c.newCell as any;
            if (
                newCell.type === "text" ||
                newCell.type === "number" ||
                newCell.type === "formula" ||
                newCell.type === "dropdown" ||
                newCell.type === "dynamicRate" ||
                newCell.type === "checkbox" ||
                newCell.type === "chevron" ||
                newCell.type === "header" ||
                newCell.type === "percentage" ||
                newCell.type === "addButton"
            ) {
                mappedChanges.push({
                    rowId: c.rowId,
                    columnId: c.columnId,
                    newCell: newCell as unknown as SheetCell,
                    previousCell: c.previousCell as unknown as SheetCell,
                });
            }
        });

        if (mappedChanges.length > 0) {
            dispatch(updateCells(mappedChanges));
            if (onCellsChanged) {
                onCellsChanged(mappedChanges);
            }
        }
    };

    const templates = useMemo(
        () => ({
            formula: new FormulaCellTemplate(),
            chevron: new ChevronCellTemplate(onChevronAction),
            dropdown: new DropdownCellTemplate(),
            dynamicRate: new DynamicRateCellTemplate(internalServiceItems),
            addButton: new AddButtonCellTemplate(
                handleButtonAction,
                serviceItems,
            ),
            iconHeader: new IconHeaderCellTemplate(),
            percentage: new PercentageCellTemplate(),
            statusDot: new StatusDotCellTemplate(),
        }),
        [
            handleButtonAction,
            serviceItems,
            internalServiceItems,
            onChevronAction,
        ],
    );

    const handleCanReorderRows = (
        targetRowId: Id,
        rowIds: Id[],
        dropPosition: any,
    ) => {
        if (rowIds.length > 1) return false;
        const targetRow = translatedRows.find(
            (r) => r.rowId === targetRowId,
        ) as any;
        const dragRow = translatedRows.find(
            (r) => r.rowId === rowIds[0],
        ) as any;
        if (!targetRow || !dragRow) return false;
        return targetRow.indentLevel === dragRow.indentLevel;
    };

    if (!currentSheet) return <div>No Sheet Active</div>;

    return (
        <div
            style={{ height: "100%", width: "100%" }}
            onContextMenu={(e) => {
                if (onCellContextMenu) {
                    e.preventDefault(); // Prevent standard browser menu
                    onCellContextMenu(e, focusedLocationRef.current);
                }
            }}
        >
            {/* ReactGrid sticky panes hardcode background-color: #ffffff.
                This bleeds through cells with border-radius. Setting to transparent
                lets the wrapper's themed background show through instead.
                Scoped to #react-grid-light so the legacy BidSheet is unaffected. */}
            <style>{`
                #react-grid-light .rg-pane.rg-pane-top,
                #react-grid-light .rg-pane.rg-pane-bottom,
                #react-grid-light .rg-pane.rg-pane-left,
                #react-grid-light .rg-pane.rg-pane-right {
                    background-color: transparent !important;
                }
            `}</style>
            <ReactGrid
                rows={translatedRows}
                columns={translatedColumns}
                customCellTemplates={templates}
                onCellsChanged={handleCellsChanged}
                enableRowSelection
                onRowsReordered={(tId, rIds, dPos) =>
                    onRowsReordered &&
                    onRowsReordered(tId as string, rIds as string[], dPos)
                }
                canReorderRows={handleCanReorderRows}
                focusLocation={focusLocation || undefined}
                onFocusLocationChanged={({ rowId, columnId }) => {
                    focusedLocationRef.current = {
                        rowId: rowId as string,
                        columnId: columnId as string,
                    };
                }}
                enableRangeSelection
                enableFillHandle
                stickyTopRows={1}
                stickyLeftColumns={1}
                stickyRightColumns={2}
            />
        </div>
    );
};
